/**
 * InviteUserModal — invite a student or teacher to a tenant.
 *
 * Mirrors the InviteModal on TenantAdmins.tsx. Same backend endpoint
 * (`/console/tenant-invites`), but role is fixed by the calling tab so the
 * UI can hide the role picker. If the email already exists they're added to
 * the tenant and the role is appended; otherwise a pending account is
 * created and a magic-link email is sent (link points to the student app
 * for `student`, the teacher app for `teacher`).
 *
 * Caller is responsible for gating visibility — invite is restricted to
 * super-admin and tenant_admin (matches inviteTenantMember's server check).
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, FormField, Input, Modal } from '@shared-ui/components';
import { userApi } from '../../api/userApi';
import { authApi } from '../../api/auth';
import { tenantsApi, PUBLIC_TENANT_ID } from '../../api/tenantsApi';

interface InviteUserModalProps {
  isOpen: boolean;
  tenantId: string;
  role: 'student' | 'teacher';
  onClose: () => void;
}

export function InviteUserModal({ isOpen, tenantId, role, onClose }: InviteUserModalProps) {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [result, setResult] = useState<{ isNewUser: boolean; emailSent: boolean } | null>(null);
  const tenantName = useTenantName(tenantId);

  const inviteMut = useMutation({
    mutationFn: () => userApi.inviteTenantMember({
      email: email.trim(),
      name: name.trim() || undefined,
      tenantId,
      role,
    }),
    onSuccess: (res) => {
      setResult({ isNewUser: res.isNewUser, emailSent: res.emailSent });
      // Refresh the list for the role we just invited into.
      queryClient.invalidateQueries({ queryKey: ['users', role] });
    },
    onError: (err: Error) => setError(err.message),
  });

  function handleClose() {
    setEmail(''); setName(''); setError(''); setResult(null);
    onClose();
  }

  function handleSubmit() {
    if (!email.trim()) return;
    setError('');
    setResult(null);
    inviteMut.mutate();
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Invite ${role} to ${tenantName}`}
      size="md"
      footer={
        result ? (
          <Button onClick={handleClose}>Done</Button>
        ) : (
          <>
            <Button variant="outline" onClick={handleClose}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              loading={inviteMut.isPending}
              disabled={!email.trim() || inviteMut.isPending}
            >
              Send invite
            </Button>
          </>
        )
      }
    >
      {!result && (
        <>
          <FormField label="Email" required>
            <Input
              type="email"
              autoFocus
              placeholder="user@example.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
            />
          </FormField>

          <FormField label="Name (optional)">
            <Input
              type="text"
              placeholder="Leave blank to use the email prefix"
              value={name}
              onChange={e => setName(e.target.value)}
            />
          </FormField>

          {error && (
            <div style={{ marginTop: 'var(--spacing-sm)', padding: 'var(--spacing-sm)', background: 'var(--status-error-bg)', color: 'var(--status-error-text)', borderRadius: 4 }}>
              {error}
            </div>
          )}

          <div style={{ marginTop: 'var(--spacing-sm)', fontSize: 12, color: 'var(--text-tertiary)' }}>
            If the email is already a BuzzBrain user they'll be added to {tenantName} directly
            and the {role} role is appended to their existing roles. Otherwise, a pending
            account is created and a sign-up email is sent — the link points to the
            {role === 'teacher' ? ' teacher' : ' student'} app.
          </div>
        </>
      )}

      {result && (
        <div style={{ padding: 'var(--spacing-sm)', background: 'var(--status-success-bg)', color: 'var(--status-success-text)', borderRadius: 4 }}>
          {result.isNewUser ? `Pending account created in ${tenantName}.` : `Existing user added to ${tenantName}.`}
          {' '}
          {result.emailSent
            ? 'Invitation email sent.'
            : 'Email delivery failed — please share the BuzzBrain link manually.'}
        </div>
      )}
    </Modal>
  );
}

/**
 * The tenant's display name, so the dialog says where the person will land —
 * an invite sent with the wrong tenant selected moves them there. Same queries
 * (and cache keys) as TenantAdmins: the tenant list is super-admin only, so a
 * tenant admin reads their own tenant from /me/tenant. Falls back to the id.
 */
function useTenantName(tenantId: string): string {
  const isSuperAdmin = authApi.getUser()?.isSuperAdmin === true;
  const tenantsQuery = useQuery({
    queryKey: ['tenants-for-tenant-admins'],
    queryFn: () => tenantsApi.list(),
    staleTime: 60_000,
    enabled: isSuperAdmin,
  });
  const myTenantQuery = useQuery({
    queryKey: ['my-tenant'],
    queryFn: () => tenantsApi.getMine(),
    staleTime: 60_000,
    enabled: !isSuperAdmin,
  });
  const name = isSuperAdmin
    ? tenantsQuery.data?.tenants.find(t => t.id === tenantId)?.name
    : (myTenantQuery.data?.id === tenantId ? myTenantQuery.data.name : undefined);
  if (name) return name;
  return tenantId === PUBLIC_TENANT_ID ? 'Public' : tenantId;
}
